import { extractPrefixedTeamId } from "@smart/bridge-types-basic";
import { loadLocale } from "@smart/itops-locale-dom";
import { buildDisplayName, formatDateTime } from "@smart/itops-utils-basic";

import {
  ReminderEligibilityInput,
  scheduleStillApplies,
} from "../../shared/submission-actions/reminder-helpers";
import { GqlTracker, ReminderScheduleValue } from "../../types";
import {
  getHistoryEventType,
  scheduledRemindersSentLabel,
  scheduledRemindersTotal,
  historyEventTypeLabel,
} from "../view/helpers";

export type SubmissionHistoryProps = {
  trackers: (GqlTracker | null)[];
  staffDetails:
    | {
        userId?: string | null;
        colorFill?: string | null;
        colorStroke?: string | null;
      }[]
    | undefined
    | null;
  automaticReminders?: boolean;
  reminderSchedule?: ReminderScheduleValue | null;
  submission?: ReminderEligibilityInput;
};

type HistoryEventCardProps = {
  label: string;
  user?: GqlTracker["user"];
  staffMemberColor?: string | null;
  sentAt?: string | null;
};

const HistoryEventCard = ({
  label,
  user,
  staffMemberColor,
  sentAt,
}: HistoryEventCardProps) => {
  const { dateDisplayFormat } = loadLocale();

  return (
    <div
      className="p-[0.8rem] rounded bg-neutral-20"
      data-testid="history-event"
    >
      <h4 className="text-ui font-semibold break-words">{label}</h4>
      {user && (
        <p className="flex items-start gap-xSmall text-caption text-neutral-600">
          by{" "}
          {staffMemberColor && (
            <div
              data-testid="staff-member-color"
              className="shrink-0 w-[1.6rem] h-[1.6rem] rounded-small"
              style={{ backgroundColor: staffMemberColor }}
            />
          )}
          <span className="min-w-0 break-words">
            {buildDisplayName(user, "team member")}
          </span>
        </p>
      )}
      {sentAt && (
        <p className="text-caption text-neutral-600 break-words">
          {formatDateTime(sentAt, dateDisplayFormat)}
        </p>
      )}
    </div>
  );
};

const submissionViewHistoryEvent = (
  tracker: GqlTracker | null,
  staffDetails: SubmissionHistoryProps["staffDetails"],
  automaticReminders: boolean | undefined,
) => {
  if (!tracker) return null;
  const eventType = getHistoryEventType(tracker, { automaticReminders });
  if (!eventType) return null;

  const staffMemberColor = staffDetails?.find(
    (sd) =>
      tracker.user?.uri &&
      sd.userId === extractPrefixedTeamId(tracker.user?.uri, "sb").id,
  )?.colorFill;

  return (
    <HistoryEventCard
      key={tracker.uri}
      label={historyEventTypeLabel[eventType]}
      user={
        eventType === "automaticRemindersStopped" ? undefined : tracker.user
      }
      staffMemberColor={staffMemberColor}
      sentAt={tracker.sentAt}
    />
  );
};

export const SubmissionHistory = ({
  trackers,
  staffDetails,
  automaticReminders,
  reminderSchedule,
  submission,
}: SubmissionHistoryProps) => {
  const scheduledReminders = automaticReminders
    ? trackers.filter((tracker) => tracker?.action === "reminder-sent-auto")
    : [];
  const scheduledTotal = reminderSchedule
    ? scheduledRemindersTotal({
        schedule: reminderSchedule,
        sent: scheduledReminders.length,
      })
    : undefined;
  const onSchedule =
    !!reminderSchedule &&
    scheduleStillApplies(submission ?? {}, reminderSchedule);

  return (
    <div className="w-[33.5rem] px-[3.2rem] py-[1.6rem] flex flex-col gap-small overflow-auto relative">
      <h2 className="text-paragraph font-semibold">History</h2>
      <div className="flex flex-col gap-small">
        {trackers.map((tracker) => {
          if (!tracker || !scheduledReminders.includes(tracker))
            return submissionViewHistoryEvent(
              tracker,
              staffDetails,
              automaticReminders,
            );
          if (scheduledTotal === undefined) return null;

          // trackers come newest first
          const position =
            scheduledReminders.length - scheduledReminders.indexOf(tracker);
          return (
            <HistoryEventCard
              key={tracker.uri}
              label={scheduledRemindersSentLabel(
                position,
                onSchedule ? scheduledTotal : scheduledReminders.length,
              )}
              sentAt={tracker.sentAt}
            />
          );
        })}
      </div>
    </div>
  );
};
